import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Edit, Trash2, Plus } from "lucide-react";
import { api, toast } from "../App";

export default function Teams({ user }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [formData, setFormData] = useState({
    team_name: "",
    department: "Projects",
    description: ""
  });

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await api.get("/teams");
      setTeams(response.data);
    } catch (error) {
      toast.error("Failed to fetch teams");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.team_name.trim()) {
      toast.error("Please enter team name");
      return;
    }
    if (!formData.department) {
      toast.error("Please select department");
      return;
    }

    try {
      if (editMode) {
        await api.put(`/teams/${currentTeam.id}`, formData);
        toast.success("Team updated successfully");
      } else {
        await api.post("/teams", formData);
        toast.success("Team created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save team");
    }
  };

  const handleEdit = (team) => {
    setCurrentTeam(team);
    setFormData({
      team_name: team.team_name,
      department: team.department,
      description: team.description || ""
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm("Are you sure you want to delete this team? Note: Employees assigned to this team should be reassigned from the Employees page.")) return;

    try {
      await api.delete(`/teams/${teamId}`);
      toast.success("Team deleted successfully");
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete team");
    }
  };

  const resetForm = () => {
    setFormData({
      team_name: "",
      department: "Projects",
      description: ""
    });
    setEditMode(false);
    setCurrentTeam(null);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-md3-on-surface-variant">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6 max-w-7xl mx-auto">
        <Card className="p-6 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-md3-headline-large text-blue-900">Teams</h1>
              <p className="text-md3-body-small text-md3-on-surface-variant mt-1">Manage department teams</p>
            </div>
            <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add Team
            </Button>
          </div>

          <Table className="bg-md3-surface-container rounded-md3-md">
            <TableHeader>
              <TableRow>
                <TableHead>Team Name</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-md3-on-surface-variant py-8">
                    No teams found. Click "Add Team" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                teams.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell className="font-medium">{team.team_name}</TableCell>
                    <TableCell>{team.department}</TableCell>
                    <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                      {team.description || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm"
                          variant="outline"
                          className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                          onClick={() => handleEdit(team)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm"
                          variant="destructive"
                          className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                          onClick={() => handleDelete(team.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Add/Edit Team Dialog */}
        <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? "Edit Team" : "Add New Team"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editMode ? "Update team details" : "Create a new team"}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="team_name" className="text-md3-label-large text-gray-700 font-medium">Team Name *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="team_name"
                  value={formData.team_name}
                  onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                  placeholder="e.g., Core Team, Support Team"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department" className="text-md3-label-large text-gray-700 font-medium">Department *</Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => setFormData({ ...formData, department: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Projects">Projects</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Estimation">Estimation</SelectItem>
                    <SelectItem value="Management">Management</SelectItem>
                    <SelectItem value="PMO">PMO</SelectItem>
                    <SelectItem value="Training">Training</SelectItem>
                    <SelectItem value="Human Resources">Human Resources</SelectItem>
                    <SelectItem value="Accounts and Finance">Accounts and Finance</SelectItem>
                    <SelectItem value="Quality Assurance">Quality Assurance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-md3-label-large text-gray-700 font-medium">Description (Optional)</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Team description or purpose"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => handleDialogOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editMode ? "Update Team" : "Create Team"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
